import { cn } from "./classNames-DcGnTh2N.js";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "./icons-ZIeZXXS7.js";
import { Skeleton } from "./internal/components/Skeleton/Skeleton.js";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Table/TableBody.tsx
const TableBody = ({ withBorderSeparator = true, className, ...props }) => {
	return /* @__PURE__ */ jsx("tbody", {
		className: cn({ "tw:[&_tr]:border-0 tw:[&_tr]:border-b-border tw:[&_tr]:border-b tw:[&_tr]:border-solid tw:[&_tr:last-child]:border-0": withBorderSeparator }, className),
		...props
	});
};
//#endregion
//#region src/components/Table/TableCell.tsx
const TableCell = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx("td", {
		className: cn("tw:px-3 tw:py-2", className),
		...props
	});
};
//#endregion
//#region src/components/Table/TableFooter.tsx
const TableFooter = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx("tfoot", {
		className: cn("tw:bg-muted/50 tw:border-t tw:border-border tw:font-medium tw:[&>tr]:last:border-b-0", className),
		...props
	});
};
//#endregion
//#region src/components/Table/TableHead.tsx
const TableHead = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx("th", {
		className: cn("tw:px-3 tw:py-2 tw:text-left tw:font-medium tw:whitespace-nowrap tw:[&:has([role=checkbox])]:pr-0 tw:[&>[role=checkbox]]:translate-y-[2px] tw:focus-within:ring-2 tw:focus-within:ring-ring/50", className),
		...props
	});
};
//#endregion
//#region src/components/Table/TableHeader.tsx
const TableHeader = ({ withShadow = false, className, ...props }) => {
	return /* @__PURE__ */ jsx("thead", {
		className: cn("tw:sticky tw:top-0 tw:z-10 tw:bg-card tw:[&_tr]:h-11 tw:[&_tr]:hover:bg-transparent tw:[&_tr]:border-b tw:[&_tr]:border-border", { "tw:shadow-table": withShadow }, className),
		...props
	});
};
//#endregion
//#region src/components/Table/TableRow.tsx
const TableRow = ({ ref, className, ...props }) => /* @__PURE__ */ jsx("tr", {
	ref,
	className: cn("tw:hover:bg-[var(--hover,color-mix(in_oklab,rgb(var(--muted))_50%,transparent))] tw:aria-selected:bg-selected tw:aria-selected:hover:bg-selected tw:aria-selected:text-[var(--selected-foreground,currentColor)]", className),
	...props
});
//#endregion
//#region src/components/Table/TableSkeletonRows.tsx
const NAME_WIDTHS = [
	"tw:w-2/5",
	"tw:w-1/3",
	"tw:w-1/2",
	"tw:w-1/4",
	"tw:w-2/5 tw:max-w-60"
];
const widthOf = (width, row) => typeof width === "string" ? width : width[row % width.length];
const SkeletonCellContent = ({ column, row }) => {
	switch (column.type) {
		case "empty": return null;
		case "name": return /* @__PURE__ */ jsxs("div", {
			className: cn("tw:flex tw:items-center", column.gapClassName ?? "tw:gap-3.5"),
			children: [column.iconClassName && /* @__PURE__ */ jsx(Skeleton, {
				height: "sm",
				className: cn("tw:shrink-0", column.iconClassName)
			}), /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1.5",
				children: [/* @__PURE__ */ jsx(Skeleton, {
					height: "xs",
					className: widthOf(column.widthClassName ?? NAME_WIDTHS, row)
				}), column.descriptionClassName && /* @__PURE__ */ jsx(Skeleton, {
					height: "xxs",
					className: widthOf(column.descriptionClassName, row)
				})]
			})]
		});
		default: return /* @__PURE__ */ jsx(Skeleton, {
			height: "xs",
			className: widthOf(column.widthClassName ?? "tw:w-3/4", row)
		});
	}
};
/**
* Placeholder rows in a table's own columns while its rows load.
*
* - Render inside `Table.Body` in place of the rows.
* - The first row announces `Loading {noun}` to screen readers; pass the plural noun, e.g. `jobs`.
* - The rows don't take the hover tint, as there is nothing to act on yet.
*/
const TableSkeletonRows = ({ columns, noun, rows = 5 }) => Array.from({ length: rows }, (_, row) => /* @__PURE__ */ jsx(TableRow, {
	className: "tw:hover:bg-transparent",
	children: columns.map((column, index) => /* @__PURE__ */ jsxs(TableCell, {
		className: column.className,
		colSpan: column.colSpan,
		children: [row === 0 && index === 0 && /* @__PURE__ */ jsx("span", {
			role: "status",
			className: "tw:sr-only",
			children: `Loading ${noun}`
		}), /* @__PURE__ */ jsx(SkeletonCellContent, {
			column,
			row
		})]
	}, index))
}, row));
//#endregion
//#region src/components/Table/TableSortHeader.tsx
/** Maps a sort direction to the `aria-sort` value for the enclosing `Table.Head`. */
const tableAriaSort = (sorted) => sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none";
/**
* Clickable column-header label with a sort-direction chevron, for use inside `Table.Head`.
*
* Presentational only — it owns no sort state. Pair it with your sort model (a `useState`
* key/direction pair, or `@tanstack/react-table`'s `getToggleSortingHandler()` / `getIsSorted()`)
* and set `aria-sort` on the enclosing `Table.Head` via {@link tableAriaSort}.
*/
const TableSortHeader = ({ sorted, children, className, ...props }) => {
	const Icon = sorted === false ? ChevronsUpDown : sorted === "asc" ? ChevronUp : ChevronDown;
	return /* @__PURE__ */ jsxs("button", {
		className: cn("tw:group tw:flex tw:cursor-pointer tw:items-center tw:gap-1 tw:border-0 tw:bg-transparent tw:p-0 tw:font-medium tw:text-inherit", className),
		...props,
		type: "button",
		children: [children, /* @__PURE__ */ jsx(Icon, { className: "tw:size-3 tw:text-muted-foreground" })]
	});
};
//#endregion
//#region src/components/Table/Table.tsx
/**
* Presentational table for rows sharing one schema; sort/filter state is the consumer's.
*
* - Not for rows of differing shape — use a `Card` list.
* - Wrap in `PageLayout.Content`.
*
* @see RECIPES.md#list-detail
*/
const Table = ({ borderCollapsed = true, bordered = false, containerClassName, className, ...props }) => {
	return /* @__PURE__ */ jsx("div", {
		className: cn("tw:relative tw:w-full", bordered && "tw:overflow-auto tw:rounded-[10px] tw:border tw:border-solid tw:border-border", containerClassName),
		children: /* @__PURE__ */ jsx("table", {
			className: cn("tw:w-full tw:caption-bottom tw:text-sm tw:text-foreground", borderCollapsed ? "tw:border-collapse" : "tw:border-separate tw:border-spacing-y-1", className),
			...props
		})
	});
};
Table.Header = TableHeader;
Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.Footer = TableFooter;
Table.SortHeader = TableSortHeader;
Table.SkeletonRows = TableSkeletonRows;
//#endregion
export { Table, TableSortHeader, tableAriaSort };

//# sourceMappingURL=Table-BlEO5CuN.js.map